import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import {BaseComponent} from "../../base.component";
import {FilterUtil} from "../../../util/filter.util";
import {ListResponse} from "../../../network/service/list.response";
import {SnackBarUtil} from "../../../util/snack.bar.util";
import {FactorDetailComponent} from '../dialog/factor-detail/factor-detail.component';
import {FactorDetailOptions} from '../dialog/factor-detail/options/factor-detail.options';
import {FactorStatusHelper} from '../../../enums/factor.status';
import {VerifyOptions} from '../../dialog/verify/options/verify.options';
import {VerifyComponent} from '../../dialog/verify/verify.component';
import {DataResponse} from '../../../network/service/data.response';
import {Subscription} from "rxjs";
import {PrintSocketService, SocketConnState} from '../../../util/service/print.socket.service';

type JobStatus = { jobId: string | null; status: string; message?: string };

@Component({
  selector: 'app-orders',
  standalone: false,
  templateUrl: './orders.component.html',
  styleUrls: ['./orders.component.scss']
})
export class OrdersComponent extends BaseComponent implements OnInit, OnDestroy {

  displayedColumns = ['Print', 'Approve', 'Info', 'FactorId', 'User', 'Price', 'Status', 'PaymentStatus', 'CreateDate', 'Branch'];
  displayDataSources = ['print', 'touch_app', 'info', 'FactorId', 'User', 'PayablePrice', 'StatusText', 'PaymentStatusText', 'CreateDate', 'BusinessId'];
  displayColumnTypes = ['event', 'event', 'event', 'string', 'string', 'price', 'status', 'status', 'date:HH:mm YYYY/MM/DD', 'business'];
  displayEndPoint = ['', '', '', '', '', ''];
  orders: any = [];
  allFilters = FilterUtil.OrdersFilters;
  filters: any = {};
  socketUrl = "http://127.0.0.1:3000";
  socketState: SocketConnState = "DISCONNECTED";
  socketError: string | null = null;
  adminId = "admin-1";
  lastQueuedJobId: string | null = null;
  statuses: JobStatus[] = [];
  sending = false;
  private sub = new Subscription();

  constructor(injector: Injector, private printSocket: PrintSocketService) {
    super(injector);
  }

  get socketBadgeClass(): string {
    switch (this.socketState) {
      case "CONNECTED":
        return "badge bg-success";
      case "CONNECTING":
        return "badge bg-danger text-dark";
      case "ERROR":
        return "badge bg-danger";
      default:
        return "badge bg-danger";
    }
  }

  get socketLabel(): string {
    switch (this.socketState) {
      case "CONNECTED":
        return "Connected";
      case "CONNECTING":
        return "Connecting...";
      case "ERROR":
        return "Error";
      default:
        return "Disconnected";
    }
  }

  override ngOnInit() {
    super.ngOnInit();
    this.sub.add(this.printSocket.getUrl().subscribe((u) => (this.socketUrl = u)));
    this.sub.add(this.printSocket.getState().subscribe((s) => (this.socketState = s)));
    this.sub.add(this.printSocket.getError().subscribe((e) => (this.socketError = e)));
    this.printSocket.connect(this.socketUrl);

    this.sub.add(
      this.printSocket.onQueued().subscribe(({jobId}) => {
        this.lastQueuedJobId = jobId;
        this.statuses.unshift({jobId, status: "QUEUED"});
      })
    );

    this.sub.add(
      this.printSocket.onStatus().subscribe((s) => {
        this.statuses.unshift(s);
        SnackBarUtil.makeMessage(this, s.status === "SUCCESS" ? 'فاکتور جهت چاپ ارسال گردید' : 'خطا در چاپ فاکتور', s.status === "SUCCESS");
        if (s.status === "SUCCESS" || s.status === "FAILED") {
          this.sending = false;
        }
      })
    );
  }

  onSocketUrlChange(value: string) {
    this.printSocket.setUrl(value);
  }

  connectSocket() {
    this.printSocket.connect(); // از url داخلی استفاده می‌کند
  }

  disconnectSocket() {
    this.printSocket.disconnect();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  override loadOnline() {
    super.loadOnline();
    this.serviceApi.getFactors(this.tableProperties.offset, this.tableProperties.limit, 'User', undefined, {
      Filter: this.filters,
      Order: this.tableProperties.order ?? {
        Field: 'ModifyDate',
        Type: 'DESC'
      }
    }).subscribe((res: ListResponse<any>) => {
      if (res.success) {
        this.orders = res.data;
        this.tableProperties.pageLength = res.count;
        this.setListeners();
      } else {
        SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
      }
    });
  }

  override async setListeners() {
    super.setListeners();
    if (this.orders) {
      for (let i = 0; i < this.orders.length; i++) {
        this.orders[i].User = this.getUserDisplayName(this.orders[i].User);
        const originalStatus = this.orders[i].Status;
        this.orders[i].StatusText = this.translateService.instant(this.getStatusText(this.orders[i].Status));
        this.orders[i].StatusColor = this.getStatusColor(originalStatus);
        this.orders[i].PaymentStatusText = this.translateService.instant(this.orders[i].BankStatusCode === 0 ? 'Paid' : 'NotPaid');
        this.orders[i].StatusColor = this.orders[i].BankStatusCode === 0 ? '#4c51af' : '#f44336';
        this.orders[i].print = async (index: number) => {
          await this.print(index);
        };
        this.orders[i].info = (index: number) => {
          this.onInfoClicked(index);
        };
        this.orders[i].touch_app = (index: number) => {
          this.onTouchAppClicked(index);
        };
      }
    }
  }

  onTouchAppClicked(index: number) {
    let verifyOptions = new VerifyOptions();
    verifyOptions.message = 'آیا پرداخت را تایید میکنید؟';
    verifyOptions.verify = true;
    let verifyComponent = this.matDialog.open(VerifyComponent);
    verifyOptions.onNoClicked = () => {
      this.serviceApi.verifiedPayment(this.orders[index].FactorId, {
        VerifyPay: false
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          verifyComponent.close();
          this.loadOnline();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    };
    verifyOptions.onYesClicked = () => {
      this.serviceApi.verifiedPayment(this.orders[index].FactorId, {
        VerifyPay: true
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          verifyComponent.close();
          this.loadOnline();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    };
    verifyComponent.componentInstance.verifyOptions = verifyOptions;
  }

  getUserDisplayName(user: any): string {
    if (!user) return '';
    const fullName = `${user.FirstName || ''} ${user.LastName || ''}`.trim();
    const mobileNumber = user.MobileNumber || '';
    if (fullName && mobileNumber) {
      return `${fullName} (${mobileNumber})`;
    } else if (fullName) {
      return fullName;
    } else if (mobileNumber) {
      return mobileNumber;
    }
    return '';
  }

  getStatusText(status: string): string {
    return FactorStatusHelper.getStatusText(status);
  }

  getStatusColor(status: string): string {
    return FactorStatusHelper.getStatusColor(status);
  }

  override pageChanged(event: any) {
    super.pageChanged(event);
    this.tableProperties.offset = (event.pageIndex);
    this.loadOnline();
  }

  override onSortChanged(event: any) {
    super.onSortChanged(event);
    this.loadOnline();
  }

  onFiltersChanged(filters: any) {
    this.filters = filters;
    this.tableProperties.offset = 0;
    this.loadOnline();
  }

  onInfoClicked(index: number) {
    let factorDetailOptions = new FactorDetailOptions();
    factorDetailOptions.factorId = this.orders[index].FactorId;
    factorDetailOptions.onFactorSelected = () => {
      this.loadOnline();
    };
    let factorDetailComponent = this.matDialog.open(FactorDetailComponent, {
      height: 'auto',
      width: '100%',
      direction: "rtl"
    });
    factorDetailComponent.componentInstance.factorDetailOptions = factorDetailOptions;
  }

  async print(index: number) {
    if (this.socketLabel === 'Connected') {
      this.serviceApi.print(this.orders[index].FactorId).subscribe(async (res: DataResponse<any>) => {
        if (res.success) {
          this.sending = true;

          try {
            await this.printSocket.sendFactorToPrinter(
              this.adminId,
              res.data
            );
          } catch (e: any) {
            this.sending = false;
            this.statuses.unshift({jobId: null, status: "FAILED", message: e?.message ?? "Send failed"});
          }
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    } else {
      SnackBarUtil.makeMessage(this, 'لطفا ابتدا از اتصال به پرینتر اطمینان حاصل نمایید', false);
    }
  }
}
